import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { principles, editorialTeam, testimonials, contactInfo, stats } from "@/mocks/site";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 黑暗爆料",
    url: "https://heianbaoliao.example.com/about",
    description: "黑暗爆料的品牌定位、内容原则、信源守则与编辑团队介绍。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "黑暗爆料",
    url: "https://heianbaoliao.example.com/",
    email: contactInfo.email,
    description: "匿名爆料与知情人揭底的聚合内容站，坚持先核实再发布，为被指涉方保留回应空间。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://heianbaoliao.example.com/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://heianbaoliao.example.com/about" },
    ],
  },
];

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "做一个克制的爆料站",
    desc: "不追求第一个发布，而追求发布之后经得起追问。每一条线索都要能说清楚：谁说的、有多少材料、哪些部分还没核实。",
  },
  {
    icon: "ri-team-line",
    title: "站在信源这一边",
    desc: "很多知情人愿意说出来，却害怕承担后果。我们把保护信源放在第一位：不留存身份、不关联真实姓名、主动建议脱敏。",
  },
  {
    icon: "ri-scales-3-line",
    title: "给被指涉方留位置",
    desc: "我们不替任何人下结论。发布前发出问询，稿件保留回应区块，即便回应与线人陈述冲突，也如实呈现。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 品牌定位、内容原则与编辑团队｜黑暗爆料",
    description:
      "了解黑暗爆料：我们如何定义匿名爆料、如何保护信源、如何核实线索、如何对待被指涉方的回应。以及编辑团队的构成与更正机制的运作方式。",
    keywords: "黑暗爆料关于我们,匿名爆料平台,信源保护,内容原则,编辑团队",
    path: "/about",
    jsonLd,
  });

  useScrollReveal([]);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "关于我们" }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h1 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
                关于黑暗爆料
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                “黑暗爆料”是许多事情的起点，也是很多麻烦的开始。我们做这个站点的初衷很朴素：当一件事在私下被反复提起，却始终没有人愿意把它写成能被查证的形状时，总得有人先动手。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们不称呼自己为媒体，也无意做任何人的裁判。我们做的事情更像整理档案——把零散的口述、单据与时间点放进同一份文档，标注清楚来源与不确定的部分，然后交给你来判断。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=editorial%20investigative%20magazine%20illustration%20of%20a%20stack%20of%20archive%20folders%20and%20a%20magnifying%20glass%20on%20a%20aged%20wooden%20desk%2C%20muted%20warm%20palette%20of%20brick%20red%20amber%20and%20charcoal%2C%20grainy%20paper%20texture%2C%20dramatic%20side%20lighting%2C%20abstract%20documentary%20collage%2C%20no%20text%2C%20high%20detail&width=1000&height=720&seq=heian-about-intro-2026&orientation=landscape"
                  alt="整理线索档案的编辑桌示意配图"
                  title="黑暗爆料 - 线索档案整理"
                  className="h-56 w-full object-cover object-top md:h-72"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {positioning.map((item) => (
            <div key={item.title} className="reveal rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
                <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="reveal mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-background-300 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={`about-${s.label}`} className="bg-background-100 px-5 py-7 text-center">
              <p className="font-heading text-3xl font-black text-primary-700">{s.value}</p>
              <p className="mt-2 text-xs font-label text-foreground-600">
                {s.label}（{s.unit}）
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">内容原则</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这四条不是口号，而是每次处理线索时都要过的检查项。任何一条过不去，稿件就不会出现在这里。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item, i) => (
              <div
                key={item.title}
                className="reveal flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-background-50">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    <span className="mr-2 font-heading text-xs text-primary-500">0{i + 1}</span>
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">编辑团队</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          团队不大，分工明确。为了信源安全，我们不公开成员的真实姓名与个人信息。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {editorialTeam.map((member) => (
            <div key={member.name} className="reveal rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                  <i className="ri-user-star-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{member.name}</h3>
                  <p className="text-xs font-label text-primary-600">{member.role}</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{member.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground-950">读者与线人怎么说</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="reveal rounded-lg border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-3xl text-accent-400" aria-hidden="true"></i>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="h-10 w-10 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={t.avatar}
                      alt={`${t.name} 头像`}
                      title={t.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span>
                    <span className="block text-sm font-label font-semibold text-foreground-900">{t.name}</span>
                    <span className="block text-xs font-label text-foreground-500">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="reveal flex flex-col gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              投稿请走匿名入口；更正、回应与合作事宜可通过公开邮箱联系。{contactInfo.channel}。
            </p>
            <p className="mt-3 flex items-center gap-2 text-sm text-foreground-800">
              <i className="ri-mail-line text-primary-600" aria-hidden="true"></i>
              <a href={`mailto:${contactInfo.email}`} className="transition-colors hover:text-primary-700 cursor-pointer">
                {contactInfo.email}
              </a>
            </p>
          </div>
          <Link
            to="/submit"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-primary-600 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-lock-line text-base" aria-hidden="true"></i>
            前往匿名投稿
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}